import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import type { MeasureDeadline } from '../../../../api/measureDeadlines';
import { fixCards } from './hrOverviewView';
import classes from './Overview.module.css';

/** A card for each kind of broken deadline or requirement. The details are on the page it links to. */
export const FixCards = ({
  overview,
  measures,
}: {
  overview: HrWorkEnvironmentOverview;
  measures: MeasureDeadline[];
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="fix-heading" className={classes.section}>
      <div className={classes.heading}>
        <Heading level={2} data-size="md" id="fix-heading">
          {t('hrOverview.fix.title')}
        </Heading>
        <Paragraph className={classes.subtle}>
          {t('hrOverview.fix.intro')}
        </Paragraph>
      </div>
      <ul className={classes.fixCards}>
        {fixCards(overview, measures, t).map((card) => (
          <li key={card.key} className={classes.fixCard}>
            <Tag
              data-size="sm"
              data-color={card.count > 0 ? 'warning' : 'success'}
              className={classes.start}
            >
              {t(
                card.count > 0 ? 'hrOverview.fix.mustFix' : 'hrOverview.fix.ok',
              )}
            </Tag>
            <span className="ds-heading" data-size="xl">
              {card.count}
            </span>
            <div className={classes.heading}>
              <Heading level={3} data-size="2xs">
                {t(`hrOverview.fix.${card.key}.title`)}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {card.text}
              </Paragraph>
            </div>
            {card.hasLink && (
              <Link asChild data-size="sm" className={classes.start}>
                <RouterLink to={card.route}>
                  {t(`hrOverview.fix.${card.key}.link`)}
                </RouterLink>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
